import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { IoMdClose } from "react-icons/io";
import FileInputExcel from "./FileInputExcel";
import ProductsExportTable from "./ProductsExportTable";
import { Product } from "@/types/ProductType";
import ErrorsList from "./ErrorsList";

type ImportExportProductProps = {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
};

export type ProductsErrors = {
  [key in keyof Product]: string[];
};
export type ProductWithErrors = Product & {
  errors: ProductsErrors;
};

type importStepOneResponse = {
  data: ProductWithErrors[];
  localData: ProductWithErrors[];
  signature: string;
};

function ImportExportProduct({
  setNeedRefetch,
  onClose,
}: ImportExportProductProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const [activeTab, setActiveTab] = useState<"import" | "export">("import");
  const modalRef = useRef<HTMLDivElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importingStep, setImportingStep] = useState<1 | 2>(1);
  const [importStepOneResponse, setImportStepOneResponse] =
    useState<importStepOneResponse | null>(null);

  const ErrorsArray = importStepOneResponse?.data
    ? importStepOneResponse.localData.flatMap((product) => {
        if (!product?.errors) return [];

        return Object.entries(product.errors)
          .filter(([key]) => key !== "options" && key !== "addons") // ignore these keys
          .flatMap(([, value]) => value)
          .filter((error): error is string => !!error);
      })
    : [];


  const deleteRow = (index: number) => {
    if (!importStepOneResponse) return;
    console.log(index);
    const updatedData = importStepOneResponse.localData.filter(
      (_, i) => i !== index,
    );
    setImportStepOneResponse({
      ...importStepOneResponse,
      localData: updatedData,
    });
  };

  const onRemoveFile = () => {
    setFile(null);
    setImportStepOneResponse(null);
    setImportingStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset file input
    }
  };

  const handleFileChange = (file: File | null) => {
    setFile(null);
    setImportStepOneResponse(null);
    setImportingStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset file input
    }
    setFile(file);
    handleImportStepOne(file);
  };

  const importUrlStepOne = `${baseUrl}/api/dashboard/products/port/import/parse?locale=${locale}`;
  const importUrlStepTwo = `${baseUrl}/api/dashboard/products/port/import?locale=${locale}`;
  const exportUrl = `${baseUrl}/api/dashboard/products/port/export?locale=${locale}`;

  const handleImportStepOne = async (file: File|null) => {
    if (!file) return;

    // Validate file type
    const validMimeTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
    ];
    const validExtensions = [".xlsx", ".xls"];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (
      !validMimeTypes.includes(file.type) ||
      !validExtensions.includes(fileExtension)
    ) {
      toast.error(t("products.invalidFileType"));
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Reset file input
      }
      return;
    }

    const formData = new FormData();
    formData.append("template", file);

    const loader = toast.loading(t("products.loading"));
    setIsImporting(true);

    try {
      const res = await axios.post(importUrlStepOne, formData, {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      });

      if (!res.data.success || res.data.error) {
        toast.error(res.data.error || t("products.importError"));
        return;
      }

      setImportStepOneResponse({
        data: JSON.parse(res.data.data)|| [],
        localData: JSON.parse(res.data.data)?.products || [],
        signature: res.data.signature,
      });
      setImportingStep(2);
      toast.success(t("products.importSuccess"));
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.error || t("products.importError")
        : t("products.unexpectedError");
      toast.error(message);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.dismiss(loader);
      setIsImporting(false);
    }
  };

  const handleImportStepTwo = async () => {
    if (!file) return;
    const loader = toast.loading(t("products.loading"));
    const body = {
      data: JSON.stringify(importStepOneResponse?.data),
      signature: importStepOneResponse?.signature,
    };
    setIsImporting(true);

    try {
      const res = await axios.post(importUrlStepTwo, body, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      if (!res.data.success) {
        toast.error(res.data.error || t("products.importError"));
        return;
      }
      toast.success(t("products.importSuccess"));
      setNeedRefetch(true);
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.error || t("products.importError")
        : t("products.unexpectedError");
      toast.error(message);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.dismiss(loader);
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    const loader = toast.loading(t("products.loading"));

    try {
      const response = await axios.get(exportUrl, {
        headers: { Authorization: token },
        responseType: "blob",
      });

      if (!response.status || response.status !== 200) {
        toast.error(response.data.error || t("products.exportError"));
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const formattedDate = new Date().toISOString().split("T")[0];
      link.download = `Exported-Prouducts_${formattedDate}.xlsx`; // Default filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(t("products.exportSuccess"));
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.error || t("products.exportError")
        : t("products.unexpectedError");
      toast.error(message);
    } finally {
      toast.dismiss(loader);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={modalRef}
        className="flex w-[95%] flex-col gap-1 rounded-lg bg-white shadow-lg md:w-fit md:min-w-[60%] md:max-w-[90%]"
      >
        <div className="flex items-center justify-between px-2 py-3 sm:px-4 sm:py-4">
          <div className="flex items-center gap-3 text-sm font-semibold sm:text-base md:text-2xl">
            <h2 className="text-lg font-semibold">
              {t("products.import_export")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mb-3 text-gray-600 hover:text-gray-800"
          >
            <IoMdClose size={24} className="text-red-600" />
          </button>
        </div>

        {/* start body */}
        <div className="thin-scrollbar flex max-h-[70dvh] flex-col gap-5 overflow-y-auto px-3 pb-3 pt-2 sm:px-5 sm:pb-4 sm:pt-1">
          {/* Tab Navigation */}
          <div className="flex rounded-md border border-gray-300">
            <button
              className={`flex-1 border-r border-gray-200 px-4 py-2 text-sm font-semibold sm:text-base ${
                activeTab === "import"
                  ? "border border-gray-200 bg-skyblue text-sidebar"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
              onClick={() => setActiveTab("import")}
            >
              {t("products.import")}
            </button>
            <button
              className={`flex-1 px-4 py-2 text-sm font-semibold sm:text-base ${
                activeTab === "export"
                  ? "border border-gray-200 bg-skyblue text-sidebar"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
              onClick={() => setActiveTab("export")}
            >
              {t("products.export")}
            </button>
          </div>

          {/* Tab Content */}
          <div className={`flex flex-col gap-5`}>
            {/* import Tab */}
            {activeTab === "import" && (
              <div className="flex items-center justify-between gap-3 whitespace-nowrap p-3 pb-1 text-sm">
                {/* Download Product Template */}
                <div className="flex w-full flex-col gap-4">
                  <a
                    href="https://interflow-production.s3.us-east-1.amazonaws.com/public/templates/products-template.xlsx"
                    className="flex w-fit items-center gap-2 rounded-lg bg-skyblue px-4 py-2 text-sm font-semibold text-sidebar transition-colors duration-200 hover:bg-sidebar hover:text-skyblue"
                  >
                    {t("products.downloadTemplate")}
                  </a>

                  <FileInputExcel
                    onFileChange={handleFileChange}
                    file={file}
                    onRemoveFile={onRemoveFile}
                    disabled={isImporting}
                  />

                  {ErrorsArray.length > 0 && (
                    <ErrorsList errors={ErrorsArray} />
                  )}

                  {importStepOneResponse?.data && (
                    <ProductsExportTable
                      products={importStepOneResponse?.localData}
                      deleteRow={deleteRow}
                      errors={ErrorsArray}
                    />
                  )}

                  <button
                    className="mt-3 flex w-fit items-center gap-2 self-end rounded-lg bg-skyblue px-4 py-2 font-semibold text-sidebar transition-colors duration-200 hover:bg-sidebar hover:text-skyblue disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-600"
                    onClick={handleImportStepTwo}
                    disabled={
                      !file ||
                      isImporting ||
                      ErrorsArray.length > 0 ||
                      (!importStepOneResponse?.localData.length &&
                        importingStep === 2)
                    }
                  >
                    {t("products.submit")}
                  </button>
                </div>
              </div>
            )}

            {/* export tab */}
            {activeTab === "export" && (
              <div className="flex items-center justify-between gap-3 self-center whitespace-nowrap p-2 text-base">
                <button
                  className="flex w-fit items-center gap-2 rounded-lg bg-skyblue px-4 py-2 font-semibold text-sidebar transition-colors duration-200 hover:bg-sidebar hover:text-skyblue"
                  onClick={handleExport}
                >
                  {t("products.exportExcel")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportExportProduct;
